import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import ActionConfirmModal from '../confirmation/ActionConfirmModal.js';
import './ExportScopeModal.scss';

/** @typedef {'current-page' | 'filtered' | 'all'} ExportScope */

const SCOPE_OPTIONS = [
  {
    key: 'current-page',
    label: 'Current page',
    description: 'Exports only the rows currently displayed.',
  },
  {
    key: 'filtered',
    label: 'Filtered results',
    description: 'Exports all rows matching the current filters.',
  },
  {
    key: 'all',
    label: 'All records',
    description: 'Exports the complete dataset.',
  },
];

function formatRowCount(count) {
  return `${Number(count).toLocaleString()} row${count === 1 ? '' : 's'}`;
}

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.onHide
 * @param {(scope: ExportScope) => void} props.onConfirm
 * @param {'csv' | 'excel' | null} [props.format]
 * @param {{ currentPage: number, filtered: number, all: number }} props.scopeCounts
 */
export default function ExportScopeModal({ show, onHide, onConfirm, format, scopeCounts }) {
  const options = useMemo(
    () =>
      SCOPE_OPTIONS.map((opt) => ({
        ...opt,
        count:
          opt.key === 'current-page'
            ? scopeCounts.currentPage
            : opt.key === 'filtered'
              ? scopeCounts.filtered
              : scopeCounts.all,
      })),
    [scopeCounts]
  );

  const firstEnabledKey = useMemo(
    () => options.find((opt) => opt.count > 0)?.key ?? 'current-page',
    [options]
  );

  const [selectedScope, setSelectedScope] = useState(firstEnabledKey);

  useEffect(() => {
    if (show) {
      setSelectedScope(firstEnabledKey);
    }
  }, [show, firstEnabledKey]);

  const selectedOption = options.find((opt) => opt.key === selectedScope);
  const canConfirm = selectedOption != null && selectedOption.count > 0;

  return (
    <ActionConfirmModal
      show={show}
      onHide={onHide}
      onConfirm={() => onConfirm(selectedScope)}
      title="Export Data"
      headerIcon={<FontAwesomeIcon icon={faCloudArrowDown} aria-hidden />}
      cancelLabel="Cancel"
      confirmLabel="Export"
      confirmDisabled={!canConfirm}
      size="md"
      backdrop="static"
      keyboard
      titleId="export-scope-modal-title"
    >
      <p className="action-confirm-modal__lead mb-3">Choose what you would like to export.</p>
      <div className="export-scope-options" role="radiogroup" aria-label="Export scope">
        {options.map((opt) => {
          const disabled = opt.count === 0;
          const inputId = `export-scope-${opt.key}`;
          return (
            <label
              key={opt.key}
              htmlFor={inputId}
              className={`export-scope-option${selectedScope === opt.key ? ' export-scope-option--selected' : ''}${disabled ? ' export-scope-option--disabled' : ''}`}
            >
              <input
                id={inputId}
                type="radio"
                name="export-scope"
                className="export-scope-option__input"
                value={opt.key}
                checked={selectedScope === opt.key}
                disabled={disabled}
                onChange={() => setSelectedScope(opt.key)}
              />
              <span className="export-scope-option__content">
                <span className="export-scope-option__title">
                  {opt.label} ({formatRowCount(opt.count)})
                </span>
                <span className="export-scope-option__description">{opt.description}</span>
              </span>
            </label>
          );
        })}
      </div>
      {format ? (
        <p className="export-scope-modal__format-hint mb-0 mt-3">
          Format: <strong>{format === 'csv' ? 'CSV' : 'Excel'}</strong>
        </p>
      ) : null}
    </ActionConfirmModal>
  );
}
